import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import AlertDialog from '../../../components/ui/AlertDialog';
import { updateRecurringRule } from '../../../api/Attendance/scheduling';
import type { RecurringShiftRuleInput, RecurringShiftRuleView } from '../../../types/scheduling';
import { extractApiErrors } from '../../../utils/apiErrors';
import { isoDateToDisplay } from '../../../utils/appDateFormat';

/**
 * CO-957 (owner, 2026-10-29): "when an edit will split a schedule, tell the user first, and make the result two
 * separate, independent schedules." The server refuses such an update with RecurringRuleSplitNeedsConfirmation
 * and the dates in `details` (yyyy-MM-dd; no end date = open-ended); the page shows them in a confirm dialog and
 * re-sends the same input with `confirmSplit: true`. Cancel sends nothing more, so the rule stays as it was.
 *
 * Owner ruling A (2026-10-29): moving a not-started schedule's start later is refused the same way, with
 * RecurringRuleGapNeedsConfirmation, when some of its employees would have days with no schedule at all
 * (nothing is stretched across schedules); the same dialog lists those days and OK re-sends with `confirmGap`.
 */
export type SplitConfirmation = {
  currentEndDate: string;
  newStartDate: string;
  newEndDate: string | null;
  newScheduleName: string;
  tailStartDate: string | null;
  tailEndDate: string | null;
  tailScheduleName: string | null;
};

const isoDay = (value: unknown): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The split the server asks to confirm, or null when `err` is any other refusal. */
export function readSplitConfirmation(err: unknown): SplitConfirmation | null {
  const refusal = extractApiErrors(err).find((e) => e?.code === 'RecurringRuleSplitNeedsConfirmation');
  if (!refusal) return null;
  const d = (refusal.details || {}) as Record<string, unknown>;
  const currentEndDate = isoDay(d.currentEndDate ?? d.CurrentEndDate);
  const newStartDate = isoDay(d.newStartDate ?? d.NewStartDate);
  // Without its dates the dialog could not say what will happen, so it is not offered.
  if (!currentEndDate || !newStartDate) return null;
  return {
    currentEndDate,
    newStartDate,
    newEndDate: isoDay(d.newEndDate ?? d.NewEndDate),
    newScheduleName: text(d.newScheduleName ?? d.NewScheduleName),
    tailStartDate: isoDay(d.tailStartDate ?? d.TailStartDate),
    tailEndDate: isoDay(d.tailEndDate ?? d.TailEndDate),
    tailScheduleName: text(d.tailScheduleName ?? d.TailScheduleName) || null,
  };
}

/** The days a later start would leave with no schedule (yyyy-MM-dd) and how many employees have some. */
export type GapConfirmation = {
  gapStartDate: string;
  gapEndDate: string;
  affectedEmployeeCount: number;
};

/** The gap the server asks to confirm, or null when `err` is any other refusal. */
export function readGapConfirmation(err: unknown): GapConfirmation | null {
  const refusal = extractApiErrors(err).find((e) => e?.code === 'RecurringRuleGapNeedsConfirmation');
  if (!refusal) return null;
  const d = (refusal.details || {}) as Record<string, unknown>;
  const gapStartDate = isoDay(d.gapStartDate ?? d.GapStartDate);
  const gapEndDate = isoDay(d.gapEndDate ?? d.GapEndDate);
  // Without its dates the dialog could not say what will happen, so it is not offered.
  if (!gapStartDate || !gapEndDate) return null;
  const count = Number(d.affectedEmployeeCount ?? d.AffectedEmployeeCount);
  return { gapStartDate, gapEndDate, affectedEmployeeCount: Number.isFinite(count) && count > 0 ? count : 1 };
}

const needsUsersFirst = (err: unknown): boolean =>
  extractApiErrors(err).some((e) => e?.code === 'RecurringRuleAssignUsersFirst');

export type AskSplitConfirmation = (split: SplitConfirmation) => Promise<boolean>;
export type AskGapConfirmation = (gap: GapConfirmation) => Promise<boolean>;
/** What useSplitConfirm hands the save: one question per confirmation the server can ask for. */
export type RuleSaveConfirmations = { ask: AskSplitConfirmation; askGap: AskGapConfirmation };

/** Returned by saveRuleConfirmingSplit when the user cancelled the split: nothing more was sent. */
export const SPLIT_CANCELLED = 'split-cancelled' as const;
/** Returned by saveRuleConfirmingSplit when the user cancelled the gap: the rule was not saved. */
export const GAP_CANCELLED = 'gap-cancelled' as const;

/**
 * CO-996: an employee change travels with the rule (`assignedUserIds`), so a refused save leaves the roster as it
 * was and nobody is notified. The one exception is a split of a schedule that goes on after the new start with
 * another period or a one-off shift: the server answers RecurringRuleAssignUsersFirst before writing anything,
 * `write` then saves the employees to the schedule, and the rule is sent again. A refusal after that leaves the
 * employees saved, so the page says so and refreshes. A v1.0.74 server, still running during a deploy, answers the
 * same way for every save that does not split, so this path also covers that window.
 */
export type DeferredUsers = { userIds: string[]; write: () => Promise<void> };

/**
 * Updates a rule; when the server says the change would split the schedule, or leave days with no schedule,
 * asks the user first and re-sends the same input confirmed. Any other refusal (including one on a confirmed
 * re-send) is thrown as usual.
 */
export async function saveRuleConfirmingSplit(
  id: number,
  input: RecurringShiftRuleInput,
  confirm: RuleSaveConfirmations,
  deferred?: DeferredUsers,
): Promise<RecurringShiftRuleView | typeof SPLIT_CANCELLED | typeof GAP_CANCELLED> {
  let sent: RecurringShiftRuleInput = deferred ? { ...input, assignedUserIds: deferred.userIds } : { ...input };
  // Each step happens at most once (employees, split, gap), so the loop ends with a save or a refusal.
  for (;;) {
    try {
      return await updateRecurringRule(id, sent);
    } catch (err) {
      if (deferred && sent.assignedUserIds && needsUsersFirst(err)) {
        await deferred.write();
        sent = { ...sent };
        delete sent.assignedUserIds;
        continue;
      }
      const split = sent.confirmSplit ? null : readSplitConfirmation(err);
      if (split) {
        if (!(await confirm.ask(split))) return SPLIT_CANCELLED;
        sent = { ...sent, confirmSplit: true };
        continue;
      }
      const gap = sent.confirmGap ? null : readGapConfirmation(err);
      if (gap) {
        if (!(await confirm.askGap(gap))) return GAP_CANCELLED;
        sent = { ...sent, confirmGap: true };
        continue;
      }
      throw err;
    }
  }
}

/** The dialog's sentences, in order. */
export function describeSplit(split: SplitConfirmation, t: TFunction): string {
  const parts = [t('ScheduleSplitConfirmCurrentEnds', { end: isoDateToDisplay(split.currentEndDate) })];
  const start = isoDateToDisplay(split.newStartDate);
  const name = split.newScheduleName;
  parts.push(split.newEndDate
    ? t('ScheduleSplitConfirmNewDated', { name, start, end: isoDateToDisplay(split.newEndDate) })
    : t('ScheduleSplitConfirmNewOpenEnded', { name, start }));
  if (split.tailStartDate) {
    const tailName = split.tailScheduleName || '';
    const tailStart = isoDateToDisplay(split.tailStartDate);
    parts.push(split.tailEndDate
      ? t('ScheduleSplitConfirmTailDated', { name: tailName, start: tailStart, end: isoDateToDisplay(split.tailEndDate) })
      : t('ScheduleSplitConfirmTailOpenEnded', { name: tailName, start: tailStart }));
  }
  return parts.join(' ');
}

/** The gap dialog's sentence. */
export function describeGap(gap: GapConfirmation, t: TFunction): string {
  return t('ScheduleGapConfirmBody', {
    start: isoDateToDisplay(gap.gapStartDate),
    end: isoDateToDisplay(gap.gapEndDate),
    employees: String(gap.affectedEmployeeCount),
  });
}

type Question =
  | { kind: 'split'; split: SplitConfirmation }
  | { kind: 'gap'; gap: GapConfirmation };

/**
 * A promise-based confirm dialog for a split or a gap: `ask(split)` / `askGap(gap)` opens it and resolves true
 * on confirm, false on cancel or dismiss. Pass the returned object to saveRuleConfirmingSplit and render
 * `dialog` once in the page.
 */
export function useSplitConfirm(): RuleSaveConfirmations & { dialog: React.ReactElement } {
  const { t } = useTranslation();
  const [question, setQuestion] = useState<Question | null>(null);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setQuestion(null);
    resolve?.(ok);
  }, []);

  const open = useCallback((next: Question) => new Promise<boolean>((resolve) => {
    // A second ask while one is open answers the first with "no" rather than leaving it pending.
    resolveRef.current?.(false);
    resolveRef.current = resolve;
    setQuestion(next);
  }), []);
  const ask = useCallback<AskSplitConfirmation>((split) => open({ kind: 'split', split }), [open]);
  const askGap = useCallback<AskGapConfirmation>((gap) => open({ kind: 'gap', gap }), [open]);

  const isGap = question?.kind === 'gap';
  let description = '';
  if (question?.kind === 'split') description = describeSplit(question.split, t);
  else if (question?.kind === 'gap') description = describeGap(question.gap, t);

  const dialog = (
    <AlertDialog
      open={!!question}
      onOpenChange={(isOpen) => { if (!isOpen) settle(false); }}
      title={t(isGap ? 'ScheduleGapConfirmTitle' : 'ScheduleSplitConfirmTitle')}
      description={description}
      variant="warning"
      confirmLabel={t(isGap ? 'ScheduleGapConfirmAction' : 'ScheduleSplitConfirmAction')}
      // Resolved here, before AlertDialog closes itself with onOpenChange(false), whose settle(false) is then a no-op.
      onConfirm={() => settle(true)}
    />
  );
  return { ask, askGap, dialog };
}
